import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import Header from 'src/components/header';
import Loader from 'src/components/loader';
import SettingsCard, { MeasurementSettingsCard } from 'src/components/SettingsCard';
import { api } from 'src/utils/api';
import config from 'src/utils/config';
import {
  AREA_UNITS,
  BUREAU_BARCODE_FORMAT,
  CONTACTLESS_LOGIN_ENABLED,
  IMPERIAL_DEFAULT_UNITS,
  LENGTH_UNITS,
  LOCALSTORAGE_KEYS,
  MEASUREMENT_SYSTEM,
  METRIC_DEFAULT_UNITS,
  VOLUME_UNITS,
  WEIGHT_UNITS,
} from 'src/utils/constants';
import {
  getPieceScanViewPreference,
  setPieceScanViewPreference,
} from 'src/utils/pieceScanViewPreference';
import Sentry from 'src/utils/sentry';
import useLocalstorage from 'src/utils/useLocalStorage';
import userPropType from 'src/utils/user-prop-type';


const AboutPage = ({ user }) => {
  const [debugMode, setDebugMode] = useLocalstorage(LOCALSTORAGE_KEYS.DEBUG_MODE, false);
  const [unitsSettings, setUnitsSettings] = useLocalstorage(LOCALSTORAGE_KEYS.UNITS_SETTINGS, {
    measurementSystem: MEASUREMENT_SYSTEM.METRIC,
    customVolumeUnit: METRIC_DEFAULT_UNITS.volume,
    customWeightUnit: METRIC_DEFAULT_UNITS.weight,
    customLengthUnit: METRIC_DEFAULT_UNITS.length,
    customAreaUnit: METRIC_DEFAULT_UNITS.area,
  });

  const { measurementSystem, customVolumeUnit, customWeightUnit, customLengthUnit, customAreaUnit } = unitsSettings;

  const CUSTOM_UNITS = {
    volume: customVolumeUnit,
    weight: customWeightUnit,
    length: customLengthUnit,
    area: customAreaUnit,
  };

  const updateUnitsSettings = (updates) => {
    setUnitsSettings((prevSettings) => ({ ...prevSettings, ...updates }));
  };

  const handleMeasurementSystemChange = (system) => {
    if (system === MEASUREMENT_SYSTEM.CUSTOM) {
      updateUnitsSettings({ measurementSystem: system });
    } else {
      const defaultUnits =
        system === MEASUREMENT_SYSTEM.METRIC ? METRIC_DEFAULT_UNITS : IMPERIAL_DEFAULT_UNITS;
      updateUnitsSettings({
        measurementSystem: system,
        customVolumeUnit: defaultUnits.volume,
        customWeightUnit: defaultUnits.weight,
        customLengthUnit: defaultUnits.length,
        customAreaUnit: defaultUnits.area,
      });
    }
  };

  const getUnit = (type) => {
    if (measurementSystem === MEASUREMENT_SYSTEM.CUSTOM) {
      return CUSTOM_UNITS[type];
    }

    const defaultUnits =
      measurementSystem === MEASUREMENT_SYSTEM.METRIC
        ? METRIC_DEFAULT_UNITS
        : IMPERIAL_DEFAULT_UNITS;

    return defaultUnits[type];
  };

  const [loading, setLoading] = useState(false);

  /* Kiosk mode is
   * visible-> when the cookie(`contactlessLoginEnabled`) is set to True.
   * not visible-> when the cookie(`contactlessLoginEnabled`) is not present(deleted)
   */
  const [cookies, setCookie, removeCookie] = useCookies([CONTACTLESS_LOGIN_ENABLED]);

  /* Show Kiosk Mode checkbox, ONLY when
   * bureau-settings for contactless_logon_enabled value is set to True in rapidfab.
   * debugMode in localStorage is set to True
   */
  const [showKioskMode, setShowKioskMode] = useState(false);

  const [isTestEventSent, setTestEventSent] = useState(false);

  const storedScanMode = localStorage.getItem(LOCALSTORAGE_KEYS.SCAN_MODE);
  const [scanMode, setScanMode] = useLocalstorage(
    LOCALSTORAGE_KEYS.SCAN_MODE,
    storedScanMode || BUREAU_BARCODE_FORMAT.QR
  );
  const [autoParsePreference, setAutoParsePreference] = useState(() => {
    const option = localStorage.getItem(LOCALSTORAGE_KEYS.AUTO_PARSE_TEMPLATE);
    return option || 'ask';
  });
  const [pieceScanViewPreference, setPieceScanViewPreferenceState] = useState(() =>
    getPieceScanViewPreference(user)
  );

  const updatePieceScanViewPreference = updates => {
    const nextPreference = { ...pieceScanViewPreference, ...updates };
    setPieceScanViewPreferenceState(nextPreference);
    setPieceScanViewPreference(user, nextPreference);
  };

  const onDebugModeChange = (checkboxEvent) => {
    setDebugMode(checkboxEvent.target.checked);
  };
  const onKioskModeChange = (checkboxEvent) => {
    if (checkboxEvent.target.checked) {
      setCookie(CONTACTLESS_LOGIN_ENABLED, true, { path: '/' });
    } else {
      removeCookie(CONTACTLESS_LOGIN_ENABLED);
    }
  };

  const sendTestEvent = () => {
    Sentry.captureMessage('Test Error', 'error');
    setTestEventSent(true);
  };

  const onAutoParsePreferenceChange = (value) => {
    setAutoParsePreference(value);
    if (value === 'ask') {
      window.localStorage.setItem(LOCALSTORAGE_KEYS.AUTO_PARSE_TEMPLATE, 'ask');
    } else {
      window.localStorage.setItem(LOCALSTORAGE_KEYS.AUTO_PARSE_TEMPLATE, value);
    }
  };

  const onScanModeSelect = (mode) => setScanMode(mode);

  const onPieceScanViewRememberChange = checkboxEvent => {
    updatePieceScanViewPreference({ remember: checkboxEvent.target.checked });
  };

  const onPieceScanViewChange = view => {
    updatePieceScanViewPreference({ view, remember: true });
  };

  useEffect(() => {
    setPieceScanViewPreferenceState(getPieceScanViewPreference(user));
  }, [user]);

  useEffect(() => {
    let getBureauSettings = () => {
      setLoading(true);
      api.get(`bureau-settings/`).json()
        .then((result) => {
          // eslint-disable-next-line camelcase
          setShowKioskMode(result?.resources[0]?.contactless_logon_enabled);
          setLoading(false);
        })
        .catch(() => {
          // Alert needed? (if the user is not logged in- 403 forbidden error)
          setLoading(false);
        });

    };
    getBureauSettings();

  }, []);


  return (
    <div className='full-height-minus-header-footer'>
      <Header title="About" user={user} />
      <main role="main">
        {!loading && (
          <dl>
            <div className='mb-2'>
              <div className="d-flex">
                <dt className="me-2 mr-2 fw-bold label">Environment:</dt>
                <dd className="mb-0 text-break text-left">{config.environment}</dd>
              </div>
              <div className="d-flex">
                <dt className="me-2 mr-2 fw-bold label">Version:</dt>
                <dd className="mb-0 text-break text-left" title={config.version}>{config.version}</dd>
              </div>
              <div className="d-flex">
                <dt className="me-2 mr-2 fw-bold label">Commit:</dt>
                <dd className="mb-0 text-break text-left" title={config.commit}>{config.commit}</dd>
              </div>
              <div className="d-flex">
                <dt className="me-2 mr-2 fw-bold label">API:</dt>
                <dd className="mb-0 text-break text-left">{config.apiHost}</dd>
              </div>
              <div className="d-flex">
                <dt className="me-2 mr-2 fw-bold label">Rapidfab:</dt>
                <dd className="mb-0 text-break text-left">{config.rapidfabHost}</dd>
              </div>
              <div className="d-flex flex-column">
                <div className='d-flex'><dt className="me-2 mr-2 fw-bold label">Sentry Enabled:</dt>
                  <dd className="mb-0 text-break text-left">{config.sentryDsn ? 'Yes' : 'No'}</dd>
                </div>
                {(config.sentryDsn && debugMode) && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={isTestEventSent}
                    onClick={sendTestEvent}
                  >
                    Send Test Event
                  </button>
                )}
              </div>
              <SettingsCard
                debugMode={debugMode}
                showKioskMode={showKioskMode}
                kioskEnabled={!!cookies.contactlessLoginEnabled}
                scanMode={scanMode}
                autoParsePreference={autoParsePreference}
                pieceScanViewRemember={pieceScanViewPreference.remember}
                pieceScanView={pieceScanViewPreference.view}
                onDebugModeChange={onDebugModeChange}
                onKioskModeChange={onKioskModeChange}
                onScanModeSelect={onScanModeSelect}
                onAutoParsePreferenceChange={onAutoParsePreferenceChange}
                onPieceScanViewRememberChange={onPieceScanViewRememberChange}
                onPieceScanViewChange={onPieceScanViewChange}
              />
            </div>

            <MeasurementSettingsCard
              measurementSystem={measurementSystem}
              volumeUnit={getUnit('volume')}
              weightUnit={getUnit('weight')}
              lengthUnit={getUnit('length')}
              areaUnit={getUnit('area')}
              volumeUnits={VOLUME_UNITS}
              weightUnits={WEIGHT_UNITS}
              lengthUnits={LENGTH_UNITS}
              areaUnits={AREA_UNITS}
              onMeasurementSystemChange={handleMeasurementSystemChange}
              onVolumeUnitChange={(value) => updateUnitsSettings({ customVolumeUnit: value })}
              onWeightUnitChange={(value) => updateUnitsSettings({ customWeightUnit: value })}
              onLengthUnitChange={(value) => updateUnitsSettings({ customLengthUnit: value })}
              onAreaUnitChange={(value) => updateUnitsSettings({ customAreaUnit: value })}
            />
          </dl>
        )}
        {loading && <Loader />}
      </main>
    </div>
  );
};

AboutPage.propTypes = {
  user: userPropType,
};

AboutPage.allowLoggedOut = true;

export default AboutPage;
